// src/context/AuthProvider.jsx
import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import AuthContext from "./AuthContext";
import { logout as logoutAction, restoreSession } from "../redux/slices/authSlice";

/**
 * AuthProvider - supplies small auth helpers used across UI.
 * Uses redux state as canonical source when available, otherwise localStorage.
 */
export default function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const reduxAuth = useSelector((s) => s.auth);

  const getAuthToken = useCallback(() => {
    return reduxAuth?.token ?? localStorage.getItem("authToken") ?? null;
  }, [reduxAuth]);

  const getMerchantId = useCallback(() => {
    return reduxAuth?.merchantId ?? localStorage.getItem("merchantId") ?? null;
  }, [reduxAuth]);

  const getParentClientId = useCallback(() => {
    return reduxAuth?.parentClientId ?? localStorage.getItem("parentClientId") ?? null;
  }, [reduxAuth]);

  const login = useCallback((token, extra = {}) => {
    // persist locally and optionally dispatch restore
    try { localStorage.setItem("authToken", token); } catch {}
    if (extra.merchantId) try { localStorage.setItem("merchantId", extra.merchantId); } catch {}
    if (extra.parentClientId) try { localStorage.setItem("parentClientId", extra.parentClientId); } catch {}
    dispatch(restoreSession({ token, merchantId: extra.merchantId, parentClientId: extra.parentClientId, exp: extra.exp }));
  }, [dispatch]);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("merchantId");
      localStorage.removeItem("parentClientId");
    } catch (e) {}
    dispatch(logoutAction());
  }, [dispatch]);

  // Stable value
  const value = useMemo(() => ({
    getAuthToken,
    getMerchantId,
    getParentClientId,
    login,
    logout,
    isAuthenticated: () => !!getAuthToken(),
  }), [getAuthToken, getMerchantId, getParentClientId, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
